import cx from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useDragScroll } from '@flipster/react/misc/browser/drag-scroll';
import { css } from '@flipster/styled-system/css';

import OpenPositionTable from './open-position-table';
import PendingPositionTable from './pending-position-table';

// 스크롤이 오른쪽으로 더 남았을 때(data-scroll-right) sticky 마지막 컬럼에 그림자 (구 .hasScrollRight)
const wrapperCss = css({
  position: 'relative',
  minHeight: 300,
  '&[data-scroll-right] td:last-child, &[data-scroll-right] th:last-child': {
    boxShadow: '-10px 0 10px 0 rgba(0, 0, 0, 0.25)',
  },
});

interface Props {
  type: 'open' | 'pending';
  className?: string;
}

export const PositionTable = ({ type, className }: Props) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const mouseEvents = useDragScroll(scrollRef);
  const [hasScrollRight, setHasScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setHasScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });

    const observer = new ResizeObserver(checkScroll);
    observer.observe(el);

    return () => {
      el.removeEventListener('scroll', checkScroll);
      observer.disconnect();
    };
  }, [checkScroll]);

  return (
    <div
      ref={scrollRef}
      className={cx(wrapperCss, className)}
      data-scroll-right={hasScrollRight || undefined}
      {...mouseEvents}
    >
      {type === 'open' && <OpenPositionTable />}
      {type === 'pending' && <PendingPositionTable />}
    </div>
  );
};
